import { supabase } from "@/lib/supabase";
import { dueDateFromTerms, isoFromDisplayDate } from "@/lib/docStatus";
import { requireAccountId } from "@/lib/profile";
import { getAccountFilesBytes, MAX_PHOTOS } from "@/lib/photoUpload";
import type { AnyDocument, DocPhoto, DocStatus, DueTermId } from "@/lib/types";

/**
 * Shared data layer over the `documents` table. Both standard quotes and
 * G702/G703 applications persist through here. The JSON `value` column holds
 * the full document object (shape defined in @/lib/types), mirroring the
 * prototype's key/value storage so the data stays compatible. RLS scopes
 * every query to the caller's account at the database level (any of its
 * up to 3 members can see/edit any document in it — see is_account_member()).
 */

export interface DocumentListItem {
  docKey: string;
  doc: AnyDocument;
  updatedAt: string;
}

async function requireUserId(): Promise<string> {
  const { data, error } = await supabase.auth.getUser();
  if (error || !data.user) throw new Error("Not signed in");
  return data.user.id;
}

/**
 * Recovers a real number from money()/moneyExact()'s formatted output
 * ("$1,234.56" or "$45") — the only place any doc_type's grandTotal ever
 * exists as a number, since the JSON blob only ever stores the formatted
 * string (backlog OPS-7). Used to populate the plain grand_total column,
 * kept in sync with the JSON on every save regardless of which builder
 * (and which formatter) wrote it.
 */
export function parseGrandTotal(formatted: string): number | null {
  const stripped = formatted.replace(/[^0-9.-]/g, "");
  if (!stripped) return null; // Number("") is 0, not NaN — a blank input must not read as a real $0 total.
  const n = Number(stripped);
  return Number.isFinite(n) ? n : null;
}

export async function getDocument(docKey: string): Promise<AnyDocument | null> {
  const { data, error } = await supabase
    .from("documents")
    .select("value")
    .eq("doc_key", docKey)
    .maybeSingle();
  if (error) throw error;
  return data ? (JSON.parse(data.value as string) as AnyDocument) : null;
}

/**
 * Storage paths present in `previousPhotos` but not `currentPhotos` — pure
 * so it's unit-testable without mocking Supabase (backlog TEST-1's sibling,
 * TEST-3: this is the same class of bug MAINT-11 was a real incident for,
 * just the inverse direction — an over-eager diff here would silently
 * delete a photo the user still has attached).
 */
export function computeDroppedPhotoPaths(
  previousPhotos: DocPhoto[] | undefined,
  currentPhotos: DocPhoto[] | undefined,
): string[] {
  const keptPaths = new Set((currentPhotos ?? []).map((p) => p.url));
  return (previousPhotos ?? []).map((p) => p.url).filter((path) => !keptPaths.has(path));
}

export async function saveDocument(docKey: string, doc: AnyDocument): Promise<void> {
  // DOCS-5: every document requires a real client. Both builders already
  // gate this in their own UI before ever constructing a document object,
  // but this is the one choke point every save (current and future) goes
  // through, so it's enforced here too rather than trusted per-caller.
  if (!doc.clientId) throw new Error("A document requires a client before it can be saved.");

  // DOCS-31: a Standard Quote's photos[] can shrink between saves (the user
  // removed one) — saveDocument() does a blind whole-document upsert with no
  // diff, so a dropped photo's Storage object was never being cleaned up,
  // orphaning it forever. Only Standard Quotes ever have photos, so every
  // other docType's save computes this and finds nothing, at the cost of
  // one extra read. Captured before the write (not after) so a failed
  // saveDocument() call never removes a Storage object the still-current,
  // unsaved document still displays.
  let droppedPhotoPaths: string[] = [];
  if (doc.docType === "standard") {
    // DOCS-32: MAX_PHOTOS is otherwise a pure client-side UI constant with
    // no backing enforcement — this is the one real per-document choke
    // point (uploadDocPhoto() runs before a new document even has a
    // docKey, so it can't enforce this itself).
    if ((doc.photos?.length ?? 0) > MAX_PHOTOS) {
      throw new Error(`A document can have at most ${MAX_PHOTOS} photos.`);
    }

    const previous = await getDocument(docKey);
    if (previous?.docType === "standard") {
      droppedPhotoPaths = computeDroppedPhotoPaths(previous.photos, doc.photos);
    }
  }

  const [user_id, account_id] = await Promise.all([requireUserId(), requireAccountId()]);
  const { error } = await supabase.from("documents").upsert(
    {
      user_id,
      account_id,
      doc_key: docKey,
      doc_type: doc.docType,
      status: doc.status,
      client_id: doc.clientId,
      value: JSON.stringify(doc),
      grand_total: parseGrandTotal(doc.grandTotal),
      due_at: doc.dueDate,
      // validUntil only exists on StandardQuote — Invoice/G702Application have no pricing-hold window.
      valid_until: doc.docType === "standard" ? (doc.validUntil ?? null) : null,
    },
    { onConflict: "account_id,doc_key" },
  );
  if (error) throw error;

  if (droppedPhotoPaths.length > 0) {
    // Best-effort, after the write succeeds: the document save is the
    // source of truth and must never be undone over a Storage cleanup
    // hiccup — same reasoning as MAINT-11's purgeAccountFiles().
    const { error: removeError } = await supabase.storage.from("account-files").remove(droppedPhotoPaths);
    if (removeError) console.error(`saveDocument(${docKey}): failed removing dropped photo(s) from Storage:`, removeError);
  }
}

/**
 * `paidDate` is only ever set when moving to "closed" (captured via the
 * paid-date dialog in the status menu, not a status-change timestamp — see
 * StandardQuote.paidDate for why). Moving away from "closed" always clears
 * it, so it never lingers as a stale answer to "when was this paid."
 */
export async function moveDocument(docKey: string, status: DocStatus, paidDate?: string): Promise<void> {
  const doc = await getDocument(docKey);
  if (!doc) return;
  doc.status = status;
  doc.paidDate = status === "closed" ? (paidDate ?? null) : null;
  await saveDocument(docKey, doc);
}

/**
 * Bumps `revision` on a document being re-edited after it's already been
 * delivered (status "pending") — called right before navigating into the
 * builder, matching the design kit's `editDocument()`. A draft never
 * bumps: nothing has left the app yet, so there's no prior copy to
 * reconcile against.
 */
export async function bumpRevisionIfSent(docKey: string): Promise<void> {
  const doc = await getDocument(docKey);
  if (!doc || doc.status !== "pending") return;
  doc.revision += 1;
  await saveDocument(docKey, doc);
}

/**
 * Records that the sub actually downloaded/printed and delivered this
 * document — confirmed via the post-print "Mark as sent" dialog, never
 * inferred from any other action (a download alone is not a delivery).
 * A draft additionally moves to "pending" here, since being delivered is
 * exactly what ends draft status.
 */
export async function markSent(docKey: string, sentDate: string): Promise<void> {
  const doc = await getDocument(docKey);
  if (!doc) return;
  doc.sentDate = sentDate;
  if (doc.status === "draft") doc.status = "pending";
  await saveDocument(docKey, doc);
}

/**
 * `markSent` plus the lineage side-effect of actually delivering a
 * revision or a converted invoice: only once a document is SENT (never on
 * draft creation, so an abandoned draft can't orphan anything) does the
 * predecessor it replaces/bills flip status — a revision's source becomes
 * `superseded`, a converted invoice's source quote becomes `invoiced`.
 * Every other "mark as sent" call site (a plain quote, a G702 application,
 * a standalone invoice) has no predecessor and behaves exactly like
 * `markSent` alone.
 *
 * Two separate Supabase writes, no transaction — if the second one fails,
 * the document itself is still correctly marked sent; the caller should
 * surface a distinct error for the predecessor-status half rather than
 * silently swallowing it.
 */
export async function sendDocument(docKey: string, sentDate: string): Promise<void> {
  const doc = await getDocument(docKey);
  await markSent(docKey, sentDate);
  if (doc?.docType === "standard" && doc.supersedesDocKey) {
    const source = await getDocument(doc.supersedesDocKey);
    if (source?.docType === "standard") {
      await saveDocument(doc.supersedesDocKey, { ...source, status: "superseded", supersededByDocKey: docKey });
    }
  } else if (doc?.docType === "invoice" && doc.derivedFromDocKey) {
    const source = await getDocument(doc.derivedFromDocKey);
    if (source?.docType === "standard") {
      await saveDocument(doc.derivedFromDocKey, { ...source, status: "invoiced" });
    }
  }
}

/**
 * Changes a document's payment terms and recomputes its due date from the
 * document's own creation date (not "now") — so Net 15 -> Net 30 extends
 * the due date from when it was sent, rather than restarting the clock.
 * "Overdue" is never set directly — only pending/accepted/closed status and
 * this due date exist in storage; see @/lib/docStatus. Quotes never get a
 * computed due date (DOCS-14) — only the term itself changes for them.
 */
export async function setDocumentTerms(docKey: string, termId: DueTermId): Promise<void> {
  const doc = await getDocument(docKey);
  if (!doc) return;
  doc.termId = termId;
  doc.dueDate = doc.docType === "standard" ? null : dueDateFromTerms(termId, isoFromDisplayDate(doc.date));
  await saveDocument(docKey, doc);
}

export async function deleteDocument(docKey: string): Promise<void> {
  const { error } = await supabase.from("documents").delete().eq("doc_key", docKey);
  if (error) throw error;
}

export async function docKeyExists(docKey: string): Promise<boolean> {
  const { data, error } = await supabase
    .from("documents")
    .select("doc_key")
    .eq("doc_key", docKey)
    .maybeSingle();
  if (error) throw error;
  return Boolean(data);
}

/** Every document regardless of status, for the /documents list and client aggregation. */
export async function getAllDocuments(): Promise<DocumentListItem[]> {
  const { data, error } = await supabase
    .from("documents")
    .select("doc_key, value, updated_at")
    .order("updated_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map((r) => ({
    docKey: r.doc_key as string,
    doc: JSON.parse(r.value as string) as AnyDocument,
    updatedAt: r.updated_at as string,
  }));
}

export interface StorageStats {
  count: number;
  bytesUsed: number;
}

/**
 * Real document count + storage used, for the app shell's storage widget
 * (previously a hardcoded "124 DOCS · 3.1 GB" fixture). Sums document bytes
 * via a server-side aggregate (PERF-7 — previously pulled every document's
 * full JSON text over the wire just to sum it client-side) plus every photo
 * in the account-files Storage bucket (DOCS-30 — previously uncounted
 * entirely). The Storage listing is best-effort: a listing failure
 * shouldn't break the whole widget over a secondary number.
 */
export async function getStorageStats(): Promise<StorageStats> {
  const accountId = await requireAccountId();
  const [{ data, error }, fileBytes] = await Promise.all([
    supabase.rpc("document_storage_stats", { p_account_id: accountId }).single(),
    getAccountFilesBytes().catch((e: unknown) => {
      console.error("getStorageStats: getAccountFilesBytes failed (continuing with 0):", e);
      return 0;
    }),
  ]);
  if (error) throw error;
  const stats = data as { doc_count: number; doc_bytes: number };
  return { count: Number(stats.doc_count), bytesUsed: Number(stats.doc_bytes) + fileBytes };
}

/** "1.2 MB", "340 KB", "0 B" — for displaying getStorageStats()'s bytesUsed. */
export function formatBytes(bytes: number): string {
  // No space before the unit (e.g. "9.8KB" not "9.8 KB") — the sidebar
  // storage widget is narrow enough that the space alone was enough to
  // wrap "9.8 KB / 100 MB" onto a second line.
  if (bytes < 1024) return `${bytes}B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)}${units[unitIndex]}`;
}
